import { useEffect, useRef, useState } from "react";

import {
  getAgentDetail,
  type AgentDetailView,
} from "../lib/marketplace-offers-api";

export interface TierUnlockRowData {
  /** The first `locked: true` ladder entry. */
  entry: AgentDetailView["ladder"][number];
  agentDisplayName?: string;
  currentTier: AgentDetailView["buyer_tier"];
}

/**
 * Plan 435 M3 (mfh-tier-unlock-row). ONE extra request
 * (`getAgentDetail(firstBrowseAgent)`) to find the first `locked: true`
 * ladder entry. Omitted (never called) when the buyer is already at T3 —
 * there is nothing a T3 buyer could unlock, so a request would only burn a
 * round trip for a row that will never render (test spec: "no
 * `getAgentDetail` call is made for the T3 case"). Any failure degrades to
 * `data: null` — the agent cards above are never affected.
 */
export function useTierUnlockRow(
  getToken: () => Promise<string | null>,
  enabled: boolean,
  proUserId: string | null,
): { data: TierUnlockRowData | null } {
  const [data, setData] = useState<TierUnlockRowData | null>(null);
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  });

  useEffect(() => {
    if (!enabled || !proUserId) {
      // Deferred past a microtask (react-hooks/set-state-in-effect) — the
      // same idiom `useHomeMarketplaceData` uses for the identical shape.
      void Promise.resolve().then(() => setData(null));
      return;
    }
    let cancelled = false;
    void getAgentDetail(getTokenRef.current, proUserId)
      .then((detail) => {
        if (cancelled) return;
        const locked = detail.ladder.find((entry) => entry.locked);
        if (!locked) {
          setData(null);
          return;
        }
        setData({
          entry: locked,
          agentDisplayName: detail.identity.display_name,
          currentTier: detail.buyer_tier,
        });
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, proUserId]);

  return { data };
}
